import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import PageHeader from "../components/PageHeader";
import { toast } from "sonner";
import { ClipboardCheck, AlertTriangle, CalendarCheck, FileWarning } from "lucide-react";
// 29/09/2026 — date gg/mm/aaaa da un'unica fonte (qui c'era dataIt).
import { formatDate } from "../lib/format";

/**
 * Regola ferrea annualita'/semestralita'.
 *
 * Quando una polizza viene registrata (a mano, da scansione, da import) con date
 * che non tornano — per esempio la RATA messa nel campo "scadenza" — entra lo
 * stesso ma finisce qui: si vedono i motivi in chiaro e, dove la regola lo
 * propone, la correzione si applica con un clic.
 */
const FREQ = { annual: "Annuale", semiannual: "Semestrale" };

export default function PoliciesToConfirmPage() {
  const [items, setItems] = useState([]);
  const [caricamento, setCaricamento] = useState(true);
  const [inCorso, setInCorso] = useState(null);

  const carica = async () => {
    try {
      const r = await axios.get("/api/policies/da-confermare");
      setItems(r.data.items || []);
    } catch (e) {
      toast.error("Non riesco a leggere le polizze da confermare");
    } finally {
      setCaricamento(false);
    }
  };

  useEffect(() => { carica(); }, []);

  const chiudi = async (policyId, applica) => {
    setInCorso(policyId);
    try {
      const r = await axios.post(`/api/policies/${policyId}/conferma-date`, { applica });
      if (r.data?.applicate) {
        toast.success(`Date corrette: scadenza ${formatDate(r.data.applicate.expiry_date || "", "—")}, rata ${formatDate(r.data.applicate.semiannual_expiry || "", "—")}`);
      } else {
        toast.success("Date confermate come sono");
      }
      setItems((p) => p.filter((x) => x.policy_id !== policyId));
    } catch (e) {
      toast.error("Non sono riuscito a chiudere la segnalazione");
    } finally {
      setInCorso(null);
    }
  };

  return (
    <>
      <PageHeader
        titolo="Polizze da confermare"
        icona={<ClipboardCheck className="h-5 w-5" />}
        descrizione="Polizze registrate con date che non tornano (annuale/semestrale): controlla il documento e sistema con un clic."
      />
      <div className="p-4 md:p-6 space-y-3">
        {caricamento ? (
          <p className="text-muted-foreground">Sto caricando…</p>
        ) : items.length === 0 ? (
          <Card>
            <CardContent className="py-10 text-center text-muted-foreground">
              <CalendarCheck className="h-8 w-8 mx-auto mb-2 text-green-600" />
              Nessuna polizza da confermare: le date sono tutte coerenti.
            </CardContent>
          </Card>
        ) : (
          items.map((x) => (
            <Card key={x.policy_id} data-testid={`da-confermare-${x.policy_id}`}>
              <CardContent className="py-4 space-y-3">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold">{x.cliente}</span>
                      <Badge variant="outline">{x.compagnia}</Badge>
                      {x.targa ? <Badge variant="secondary">{x.targa}</Badge> : null}
                      <Badge>{FREQ[x.frequenza] || x.frequenza || "—"}</Badge>
                      <span className="text-xs text-muted-foreground">polizza {x.policy_number}</span>
                    </div>
                    <div className="text-sm text-muted-foreground mt-1">
                      decorrenza <b>{formatDate(x.decorrenza, "—")}</b> · scadenza <b>{formatDate(x.scadenza, "—")}</b> · rata <b>{formatDate(x.rata, "—")}</b>
                    </div>
                  </div>
                </div>

                <div className="rounded-md bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 p-3 text-sm">
                  <div className="flex items-center gap-2 font-medium text-amber-800 dark:text-amber-300">
                    <AlertTriangle className="h-4 w-4" /> Perché è da confermare
                  </div>
                  <ul className="list-disc ml-6 mt-1 text-amber-900 dark:text-amber-200">
                    {(x.motivi || []).map((m, i) => <li key={i}>{m}</li>)}
                  </ul>
                </div>

                {x.proposta ? (
                  <div className="rounded-md border p-3 text-sm">
                    <div className="font-medium flex items-center gap-2">
                      <FileWarning className="h-4 w-4" /> Correzione proposta
                    </div>
                    <div className="text-muted-foreground mt-1">
                      scadenza <b>{formatDate(x.proposta.expiry_date, "—")}</b> · rata <b>{formatDate(x.proposta.semiannual_expiry, "—")}</b>
                      {x.proposta.nota ? <span className="block text-xs mt-1">{x.proposta.nota}</span> : null}
                    </div>
                  </div>
                ) : null}

                <div className="flex gap-2 flex-wrap">
                  {x.proposta ? (
                    <Button size="sm" disabled={inCorso === x.policy_id} onClick={() => chiudi(x.policy_id, true)} data-testid={`applica-${x.policy_id}`}>
                      Applica la correzione
                    </Button>
                  ) : null}
                  <Button size="sm" variant="outline" disabled={inCorso === x.policy_id} onClick={() => chiudi(x.policy_id, false)} data-testid={`lascia-${x.policy_id}`}>
                    Lascia come sono
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => window.open(`/policies/${x.policy_id}`, "_blank")}>
                    Apri la polizza
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>
    </>
  );
}
